// Formatting and small DOM helpers, taken over from the prototype.

export const $ = <T extends Element = HTMLElement>(s: string, r: ParentNode = document) => r.querySelector<T>(s)!;

export const esc = (v: unknown) =>
  String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

/** Today in Berlin (YYYY-MM-DD), not in UTC: between midnight and 01:00/02:00 that is still yesterday. */
export const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });

/** The user is typing: a text field, text area or list has the focus (buttons and checkboxes do not count). */
export function isTyping(within?: Element | null): boolean {
  const a = document.activeElement as HTMLElement | null;
  if (!a || (within && !within.contains(a))) return false;
  return a.matches("textarea, select, input:not([type=button]):not([type=submit]):not([type=checkbox]):not([type=radio]):not([type=file])");
}

export const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return "–";
  const d = new Date(iso);
  return isNaN(+d) ? esc(iso) : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

export const fmtDT = (iso: string | null | undefined) => {
  if (!iso) return "–";
  const d = new Date(iso);
  return isNaN(+d) ? esc(iso) : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export function daysSince(iso: string | null | undefined): number {
  if (!iso) return 9999;
  return (Date.now() - +new Date(iso)) / 864e5;
}

export function ago(iso: string | null | undefined): string {
  if (!iso) return "keine Aktivität";
  const d = daysSince(iso);
  if (d < 1) return "heute";
  if (d < 2) return "gestern";
  return `vor ${Math.floor(d)} T.`;
}

export const num = (v: unknown): v is number => typeof v === "number" && isFinite(v);

export const fmtKg = (v: number | null | undefined) =>
  !num(v)
    ? "–"
    : v >= 1000
      ? `${(v / 1000).toLocaleString("de-DE", { maximumFractionDigits: 2 })} t`
      : `${v.toLocaleString("de-DE", { maximumFractionDigits: 0 })} kg`;

export const fmtTEUR = (v: number | null | undefined) =>
  !num(v) ? "–" : v >= 1000 ? `${Math.round(v / 1000).toLocaleString("de-DE")} T€` : `${v.toLocaleString("de-DE")} €`;

export const fmtSize = (b: number | null | undefined) =>
  !num(b) ? "" : b >= 1024 * 1024 ? `${(b / 1024 / 1024).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

/** Imported texts without separator lines (e.g. of Teams invitations) and without runs of blank lines. */
export const cleanText = (s: string | null | undefined) =>
  (s ?? "")
    .replace(/^[ \t]*[_\-–—=*]{8,}[ \t]*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

export function emptyState(title: string, text: string): string {
  return `<div class="empty"><b>${esc(title)}</b>${esc(text)}</div>`;
}

/**
 * Puts new content into `el` and keeps where its scrolling part `sel` stood (keep = false: back to the start). The
 * minute poll redraws under the reader's eyes; a fresh element would start at the top and the left.
 */
export function redraw(el: Element, html: string, sel: string, keep = true): void {
  const old = keep ? el.querySelector(sel) : null;
  const top = old?.scrollTop ?? 0;
  const left = old?.scrollLeft ?? 0;
  el.innerHTML = html;
  const box = el.querySelector(sel);
  if (box) {
    box.scrollTop = top;
    box.scrollLeft = left;
  }
}

/** A short message at the bottom; `ms` longer for one that must be read (a failure with its reason). */
export function toast(msg: string, ms = 2800): void {
  const t = $("#toast");
  t.textContent = msg;
  t.hidden = false;
  window.clearTimeout(Number(t.dataset.timer));
  t.dataset.timer = String(window.setTimeout(() => (t.hidden = true), ms));
}

/**
 * A number typed the German way: comma for decimals, dots only as thousands separators ("1.250,5"). A lone dot
 * with other than three digits after it is a decimal point ("12.5" from a prefilled field). NaN when empty or no
 * number.
 */
export function parseDeNumber(s: string): number {
  const t = s.trim().replace(/\s/g, "");
  if (!t) return NaN;
  if (t.includes(",")) return Number(t.replace(/\./g, "").replace(",", "."));
  return /^-?\d{1,3}(\.\d{3})+$/.test(t) ? Number(t.replace(/\./g, "")) : Number(t);
}

/** A number for an input field the German way, without grouping (12,5 instead of 12.5). */
export const inputNumber = (n: number) => n.toLocaleString("de-DE", { useGrouping: false, maximumFractionDigits: 10 });
